"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import Icon from "@/components/ui/AppIcon";

const navLinks = [
  { label: "Home", href: "/homepage" },
  { label: "How It Works", href: "/homepage#how-it-works" },
  { label: "For Clinics", href: "/homepage#for-clinics" },
  { label: "About", href: "/homepage#about" },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-warm-md border-b border-warm-border-light"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-18 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2.5 group" aria-label="BabyBank Home">
          <div className="relative">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
              style={{ background: "linear-gradient(135deg, #2D6A8F 0%, #5A8A6F 100%)" }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 3C10 3 6 5.5 6 9.5C6 11.985 7.79 14 10 14C12.21 14 14 11.985 14 9.5C14 5.5 10 3 10 3Z" fill="white" fillOpacity="0.9"/>
                <path d="M7 13.5C5.5 14.5 4.5 16 4.5 17.5H15.5C15.5 16 14.5 14.5 13 13.5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
          </div>
          <span className="font-heading font-700 text-xl text-ink tracking-tight" style={{ fontWeight: 700 }}>
            Baby<span className="text-primary">Bank</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                pathname === link?.href?.split("#")?.[0]
                  ? "text-primary bg-primary-pale" :"text-ink-muted hover:text-ink hover:bg-warm-surface"
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </div>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            href="/sign-up"
            className="px-4 py-2 rounded-full text-sm font-medium text-ink-muted hover:text-ink transition-colors duration-200"
          >
            Sign In
          </Link>
          <Link href="/create-campaign" className="btn-primary text-sm px-5 py-2.5">
            Start Campaign
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden w-10 h-10 rounded-xl flex items-center justify-center text-ink-muted hover:text-ink hover:bg-warm-surface transition-colors duration-200"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          <Icon name={mobileOpen ? "XMarkIcon" : "Bars3Icon"} size={22} />
        </button>
      </nav>
      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          mobileOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-white/95 backdrop-blur-xl border-t border-warm-border-light px-4 py-6 space-y-1">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className={`block px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                pathname === link?.href?.split("#")?.[0]
                  ? "text-primary bg-primary-pale" :"text-ink-muted hover:text-ink hover:bg-warm-surface"
              }`}
            >
              {link?.label}
            </Link>
          ))}
          <div className="pt-4 space-y-2 border-t border-warm-border-light mt-4">
            <Link href="/sign-up" className="block w-full text-center px-4 py-3 rounded-xl text-sm font-medium text-ink-muted hover:text-ink hover:bg-warm-surface transition-colors duration-200">
              Sign In
            </Link>
            <Link href="/create-campaign" className="btn-primary w-full justify-center text-sm">
              Start Campaign
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}